import axios from 'axios';

const BACKEND_URL = (process.env.REACT_APP_BACKEND_URL || "").trim();

const API_URL = BACKEND_URL ? `${BACKEND_URL.replace(/\/$/, "")}/api` : "/api";

export const getMediaUrl = (urlOrPath) => {
  if (!urlOrPath) return '';
  if (urlOrPath.startsWith('http://') || urlOrPath.startsWith('https://')) return urlOrPath;
  const base = BACKEND_URL || '';
  const path = urlOrPath.startsWith('/') ? urlOrPath : `/${urlOrPath}`;
  return `${base}${path}`;
};

if (process.env.NODE_ENV === "development") {
  if (BACKEND_URL) {
    console.info(`Using API base '${API_URL}'.`);
  } else {
    console.info("Using relative API base '/api' (proxy/same-origin mode).");
  }
}

const api = axios.create({
  baseURL: API_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add auth token to requests
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('vennli_token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Handle auth errors
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('vennli_token');
      localStorage.removeItem('vennli_user');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

// Auth
export const register = (data) => api.post('/auth/register', data);
export const login = (data) => api.post('/auth/login', data);
export const getMe = () => api.get('/auth/me');
export const updateProfile = (data) => api.put('/auth/profile', data);
export const verifyEmail = (token) => api.post('/auth/verify-email', { token });
export const resendVerification = (language) => api.post('/auth/resend-verification', { language });
export const forgotPassword = (email, language) => api.post('/auth/forgot-password', { email, language });
export const resetPassword = (token, password) => api.post('/auth/reset-password', { token, password });

// Services
export const getCategories = () => api.get('/services/categories');
export const getAllCategories = () => api.get('/services/categories/all');

// Service Listings (Helper offerings)
export const getServiceListings = (params) => api.get('/services/listings', { params });
export const getServiceListing = (id) => api.get(`/services/listings/${id}`);
export const getMyServiceListings = () => api.get('/services/listings/my');
export const createServiceListing = (data) => api.post('/services/listings', data);
export const updateServiceListing = (id, data) => api.put(`/services/listings/${id}`, data);
export const deleteServiceListing = (id) => api.delete(`/services/listings/${id}`);
export const addListingMedia = (listingId, media) => api.post(`/services/listings/${listingId}/media`, media);
export const removeListingMedia = (listingId, mediaId) => api.delete(`/services/listings/${listingId}/media/${mediaId}`);
export const getHelperListings = (helperId) => api.get(`/helpers/${helperId}/listings`);

// Helpers
export const getHelpers = (params) => api.get('/helpers', { params });
export const getHelper = (id) => api.get(`/helpers/${id}`);
export const getHelperReviews = (id) => api.get(`/helpers/${id}/reviews`);
export const getBookingReviews = (bookingId) => api.get(`/reviews/booking/${bookingId}`);
export const getCareSeekerReviews = (userId) => api.get(`/reviews/care-seeker/${userId}`);
export const getMyHelperProfile = () => api.get('/my/helper-profile');
export const updateMyHelperProfile = (data) => api.put('/my/helper-profile', data);

// Groups
export const getGroups = (params) => api.get('/groups', { params });
export const getGroup = (id) => api.get(`/groups/${id}`);
export const createGroup = (data) => api.post('/groups', data);
export const updateGroup = (id, data) => api.put(`/groups/${id}`, data);
export const joinGroup = (id) => api.post(`/groups/${id}/join`);
export const leaveGroup = (id) => api.post(`/groups/${id}/leave`);
export const deleteGroup = (id) => api.delete(`/groups/${id}`);
export const getMyGroups = () => api.get('/my/groups');
export const getGroupReviews = (id) => api.get(`/groups/${id}/reviews`);
export const requestToJoinGroup = (id, data) => api.post(`/groups/${id}/join`, data);
export const getJoinRequests = (id) => api.get(`/groups/${id}/requests`);
export const approveJoinRequest = (groupId, requestId, data) => api.post(`/groups/${groupId}/requests/${requestId}/approve`, data);
export const rejectJoinRequest = (groupId, requestId, data) => api.post(`/groups/${groupId}/requests/${requestId}/reject`, data);
export const replyToJoinRequest = (groupId, requestId, data) => api.post(`/groups/${groupId}/requests/${requestId}/reply`, data);
export const inviteToGroup = (id, data) => api.post(`/groups/${id}/invite`, data);
export const getGroupInvites = (id) => api.get(`/groups/${id}/invites`);
export const acceptGroupInvite = (groupId, token) => api.post(`/groups/${groupId}/accept-invite?token=${token}`);
export const getMyRequestStatus = (id) => api.get(`/groups/${id}/my-request-status`);

// Care seeker profile
export const getCareSeekerProfile = (userId) => api.get(`/care-seekers/${userId}`);

// Jobs
export const getJobs = (params) => api.get('/jobs', { params });
export const getJob = (id) => api.get(`/jobs/${id}`);
export const getMyJobs = () => api.get('/jobs/my');
export const createJob = (data) => api.post('/jobs', data);
export const updateJob = (id, data) => api.put(`/jobs/${id}`, data);
export const deleteJob = (id) => api.delete(`/jobs/${id}`);
export const addJobMedia = (jobId, media) => api.post(`/jobs/${jobId}/media`, media);
export const removeJobMedia = (jobId, mediaId) => api.delete(`/jobs/${jobId}/media/${mediaId}`);
export const applyToJob = (jobId, data) => api.post(`/jobs/${jobId}/apply`, data);
export const getJobApplications = (jobId) => api.get(`/jobs/${jobId}/applications`);
export const getMyApplications = () => api.get('/my/applications');
export const updateApplicationStatus = (appId, status) => api.put(`/applications/${appId}/status?status=${status}`);

// File Upload
export const uploadFile = (file) => {
  const formData = new FormData();
  formData.append('file', file);
  return api.post('/upload', formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  });
};
export const deleteFile = (fileId) => api.delete(`/upload/${fileId}`);

// Bookings
export const createBooking = (data) => api.post('/bookings', data);
export const getBookings = () => api.get('/bookings');
export const getBooking = (id) => api.get(`/bookings/${id}`);
export const updateBookingStatus = (id, status) => api.put(`/bookings/${id}/status?status=${status}`);
export const approveBooking = (id) => api.post(`/bookings/${id}/approve`);
export const declineBooking = (id) => api.post(`/bookings/${id}/decline`);
export const completeBookingStatus = (id) => api.post(`/bookings/${id}/complete`);

// Messages
export const getBookingMessages = (bookingId) => api.get(`/messages/booking/${bookingId}`);
export const sendBookingMessage = (bookingId, data) => api.post(`/messages/booking/${bookingId}`, data);

// Notifications
export const getNotifications = (unread = false) => api.get(`/notifications${unread ? '?unread=true' : ''}`);
export const markNotificationRead = (notificationId) => api.put(`/notifications/${notificationId}/read`);
export const markAllNotificationsRead = () => api.put('/notifications/read-all');

// Notification Preferences
export const getNotificationPreferences = () => api.get('/users/notification-preferences');
export const updateNotificationPreferences = (data) => api.put('/users/notification-preferences', data);

// Availability
export const getMyAvailability = (month) => api.get(`/availability/me?month=${month}`);
export const updateMyAvailability = (data) => api.put('/availability/me', data);
export const deleteMyOverride = (date) => api.delete(`/availability/me/override?date=${date}`);
export const getHelperAvailability = (helperId, month) => api.get(`/availability/helper/${helperId}?month=${month}`);
export const getDefaultAvailability = () => api.get('/availability/defaults');
export const updateDefaultAvailability = (data) => api.put('/availability/defaults', data);

// Admin
export const getAdminOverview = () => api.get('/admin/overview');
export const getAdminUsers = () => api.get('/admin/users');
export const updateAdminUserRole = (userId, role) => api.put(`/admin/users/${userId}/role`, { role });
export const getAdminBookings = () => api.get('/admin/bookings');
export const updateAdminBookingStatus = (bookingId, status) => api.put(`/admin/bookings/${bookingId}/status`, { status });
export const getAdminJobs = () => api.get('/admin/jobs');
export const deleteAdminJob = (jobId) => api.delete(`/admin/jobs/${jobId}`);
export const getAdminGroups = () => api.get('/admin/groups');
export const deleteAdminGroup = (groupId) => api.delete(`/admin/groups/${groupId}`);
export const getAdminListings = () => api.get('/admin/service-listings');
export const deleteAdminListing = (listingId) => api.delete(`/admin/service-listings/${listingId}`);
export const getAdminPayments = () => api.get('/admin/payments');

// Reviews
export const createReview = (data) => api.post('/reviews', data);

// Payments
export const createCheckout = (data) => api.post('/payments/checkout', data);
export const getPaymentStatus = (sessionId) => api.get(`/payments/status/${sessionId}`);
export const getBookingBreakdown = (bookingId) => api.get(`/payments/booking/${bookingId}/breakdown`);

// Stripe Connect
export const createConnectAccount = () => api.post('/payments/connect/create-account');
export const createConnectAccountLink = () => api.post('/payments/connect/create-account-link');
export const getConnectStatus = () => api.get('/payments/connect/status');

// Stats
export const getStats = () => api.get('/stats');

// Vennli Ung
export const submitUngSignup = (data) => api.post('/ung/signup', data);

// Feedback / Intake
export const submitFeedback = (data) => api.post('/intake/submit', data);
export const getAdminIntake = (params) => api.get('/admin/intake', { params });
export const getAdminIntakeStats = () => api.get('/admin/intake/stats');
export const getAdminIntakeDetail = (id) => api.get(`/admin/intake/${id}`);
export const updateAdminIntake = (id, data) => api.patch(`/admin/intake/${id}`, data);

// Economy
export const getEconomySummary = (year) => api.get(`/economy/summary?year=${year}`);
export const getEconomyTransactions = (year, limit = 20) => api.get(`/economy/transactions?year=${year}&limit=${limit}`);
export const updateFrikortLimit = (limit) => api.put('/economy/frikort', { frikort_limit_nok: limit });
export const downloadEconomyReport = (year) => api.get(`/economy/report.csv?year=${year}`, { responseType: 'blob' });

export default api;
